import { Code, ConnectError } from '@connectrpc/connect'
import { BiddingIntradayOrderWithdrawState } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import {
  BiddingIntradayOrderBulkWithdrawFailureReason,
  type WithdrawBiddingIntradayOrdersResponse,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'

import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import { composeIntradayTableStatus } from '~/pages/IntradayBidding/models/intradayTableStatus'

/**
 * 一括板取消 (AC-BIN-112) の対象。 入札一覧の絞り込み (AC-BIN-102 + AC-BIN-018 =
 * `filteredBids`。 売買逆転のみ表示 AC-BIN-046 は含めない) に合う札のうち、 一覧のステータスが
 * 入札中で、 1 件の取消ボタンが出る (`withdrawable`) 札。
 *
 * 取消処理中の札は取消の操作が済んでいる (backend が取下不可で返す) ので入らない。
 * 取消失敗の札は取り消し直せるので入る。 要確認の札 (AC-BIN-127) は入札中のままなので入る。
 * 予約中・送信中・最終結果・下書き・承認待ちは一覧のステータスが入札中でないので入らない。
 */
export function intradayBulkWithdrawTargets(
  filteredBids: readonly IntradayBidRow[],
): IntradayBidRow[] {
  return filteredBids.filter(
    (bid) => composeIntradayTableStatus(bid) === 'waiting' && bid.withdrawable,
  )
}

/**
 * 「入札中の札を一括で板取消」 を選べない理由のうち、 一覧の札で決まるもの (AC-BIN-112 の表)。
 * 対象があれば undefined。 絞り込みに合う入札中の札があって、 そのすべてが取消処理中なら
 * `allPending`、 それ以外の 0 件は `noTarget`。 読み取り専用の理由と一覧の取得前 (理由を出さない)
 * はこれより優先し、 呼び出し側が持つ。
 */
export type IntradayBulkWithdrawBlockedCause = 'noTarget' | 'allPending'

export function intradayBulkWithdrawBlockedCause(
  filteredBids: readonly IntradayBidRow[],
): IntradayBulkWithdrawBlockedCause | undefined {
  if (intradayBulkWithdrawTargets(filteredBids).length > 0) return undefined
  const active = filteredBids.filter(
    (bid) => composeIntradayTableStatus(bid) === 'waiting',
  )
  return active.length > 0 &&
    active.every(
      (bid) => bid.withdrawState === BiddingIntradayOrderWithdrawState.PENDING,
    )
    ? 'allPending'
    : 'noTarget'
}

/**
 * 受け付けなかった理由 (バナーの文と「詳細を見る」 の札ごとの理由。 AC-BIN-112)。 backend は
 * 1 札に 1 つの理由 (見つからない → GC → 照合 → 状態の順に当てたもの) を返す。
 */
export type IntradayBulkWithdrawRefusal =
  | 'updatedByOther'
  | 'gateClosed'
  | 'notWithdrawable'

const REFUSAL_OF: Record<
  BiddingIntradayOrderBulkWithdrawFailureReason,
  IntradayBulkWithdrawRefusal
> = {
  [BiddingIntradayOrderBulkWithdrawFailureReason.UPDATED_BY_OTHER]:
    'updatedByOther',
  [BiddingIntradayOrderBulkWithdrawFailureReason.GATE_CLOSED]: 'gateClosed',
  // 見つからない札も、 利用者の次の行動 (一覧を確かめる) が同じなので取り消せない状態の側に入れる。
  [BiddingIntradayOrderBulkWithdrawFailureReason.NOT_WITHDRAWABLE]:
    'notWithdrawable',
  [BiddingIntradayOrderBulkWithdrawFailureReason.NOT_FOUND]: 'notWithdrawable',
  [BiddingIntradayOrderBulkWithdrawFailureReason.UNSPECIFIED]:
    'notWithdrawable',
}

/** 一括板取消の応答の読み方。 */
export type IntradayBulkWithdrawOutcome = {
  /** 受け付けた札の id (結果を追う札)。 */
  acceptedIds: string[]
  /**
   * 受け付けなかった札があった時のバナーの中身。 全件受け付けた時は undefined (バナーを出さない)。
   */
  refused:
    | {
        acceptedCount: number
        bids: Array<{
          bid: IntradayBidRow
          reason: IntradayBulkWithdrawRefusal
        }>
      }
    | undefined
}

/**
 * 応答の結果 (送った対象と同じ順・同じ件数) を、 受け付けた札と受け付けなかった札に分ける。
 * 結果の無い札は受け付けていない側 (取り消せる状態ではない) に入れる。
 */
export function readIntradayBulkWithdrawResponse(
  response: Pick<WithdrawBiddingIntradayOrdersResponse, 'results'>,
  targets: readonly IntradayBidRow[],
): IntradayBulkWithdrawOutcome {
  const resultById = new Map(response.results.map((r) => [r.id, r.outcome]))
  const acceptedIds: string[] = []
  const refusedBids: Array<{
    bid: IntradayBidRow
    reason: IntradayBulkWithdrawRefusal
  }> = []
  for (const bid of targets) {
    const outcome = resultById.get(bid.id)
    if (outcome?.case === 'accepted') {
      acceptedIds.push(bid.id)
      continue
    }
    refusedBids.push({
      bid,
      reason:
        REFUSAL_OF[
          outcome?.case === 'failure'
            ? outcome.value.reason
            : BiddingIntradayOrderBulkWithdrawFailureReason.UNSPECIFIED
        ],
    })
  }
  return {
    acceptedIds,
    refused:
      refusedBids.length === 0
        ? undefined
        : { acceptedCount: acceptedIds.length, bids: refusedBids },
  }
}

/**
 * バナーの本文の文の並び (AC-BIN-112 の表の順: GC を過ぎた → ほかの操作で更新されていた →
 * 取り消せる状態ではない)。
 */
const REFUSAL_ORDER: readonly IntradayBulkWithdrawRefusal[] = [
  'gateClosed',
  'updatedByOther',
  'notWithdrawable',
]

/**
 * バナーの本文の文 (理由ごとの件数。 0 件の理由は出さない)。 1 札は backend が返した 1 つの
 * 理由にだけ数える。
 */
export function intradayBulkWithdrawRefusalCounts(
  bids: ReadonlyArray<{ reason: IntradayBulkWithdrawRefusal }>,
): Array<{ reason: IntradayBulkWithdrawRefusal; count: number }> {
  return REFUSAL_ORDER.map((reason) => ({
    reason,
    count: bids.filter((b) => b.reason === reason).length,
  })).filter((row) => row.count > 0)
}

/**
 * 一括板取消の要求そのものが失敗した時の区分 (AC-BIN-112)。
 * - `workflowClosed`: 開いた後にワークフローが完了・削除されていた (この RPC で FailedPrecondition を
 *   返すのはワークフローケースの検査だけ)。 どの札も取り消していない
 * - `refused`: それ以外でサーバーが要求の全体を受け付けなかった。 どの札も取り消していない
 * - `unknown`: 通信の失敗でサーバーの応答が無く、 受け付けたかが分からない
 */
export type IntradayBulkWithdrawRequestFailure =
  | 'workflowClosed'
  | 'refused'
  | 'unknown'

const UNKNOWN_RESULT_CODES: ReadonlySet<Code> = new Set([
  Code.Unavailable,
  Code.DeadlineExceeded,
  Code.Canceled,
  Code.Unknown,
])

export function intradayBulkWithdrawRequestFailure(
  error: unknown,
): IntradayBulkWithdrawRequestFailure {
  const { code } = ConnectError.from(error)
  if (code === Code.FailedPrecondition) return 'workflowClosed'
  return UNKNOWN_RESULT_CODES.has(code) ? 'unknown' : 'refused'
}

/**
 * 通信の失敗で受け付けたかが分からない時に、 結果を追う札 (AC-BIN-112)。 読み直した一覧で取消処理中に
 * なっている送った札だけを返す (受け付けられていた札)。 送る前から取消失敗だった札は、 要求が届いて
 * いなければ取消失敗のままなので追わない (新しい失敗と数えないため)。
 */
export function intradayBulkWithdrawPendingIds(
  items: ReadonlyArray<{
    id: string
    withdrawState?: BiddingIntradayOrderWithdrawState
  }>,
  sentIds: readonly string[],
): string[] {
  const pending = new Set(
    items
      .filter(
        (item) =>
          item.withdrawState === BiddingIntradayOrderWithdrawState.PENDING,
      )
      .map((item) => item.id),
  )
  return sentIds.filter((id) => pending.has(id))
}
